import {
  CubeIcon,
  CurrencyDollarIcon,
  GlobeAmericasIcon as GlobeIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"

import { Status, useInsightPanelDataFetcher } from "./useInsightPanelDataFetcher"
import { Bee } from "~/src/components/BeeKit"
import {
  InsightsPanel,
  InsightsPanelContent,
  InsightsPanelErrorDefault,
  InsightsPanelHeader,
} from "~/src/components/Dashboard/InsightsPanel"
import { Format } from "~/src/lib/format"
import { WithErrors } from "~/src/types/withErrors"

export type OverviewStats = {
  activeUserCount: number
  orderCount: number
  averageOrderCost: number
  countriesShippedToCount: number
}

export type OverviewData = WithErrors<OverviewStats>

export type StorefrontInsightsOverviewPanelRenderProps = {
  status: Status
  data?: OverviewData
}

export function StorefrontInsightsOverviewPanel() {
  const { isLoading, data } = useInsightPanelDataFetcher<OverviewData>(`overview`)

  let status: Status
  if (isLoading) status = "loading"
  else if (data?.errors) status = "error"
  else if (data) status = "success"
  else status = "noData"

  return <StorefrontInsightsOverviewRenderPanel status={status} data={data} />
}

export function StorefrontInsightsOverviewRenderPanel({ status, data }: StorefrontInsightsOverviewPanelRenderProps) {
  return (
    <InsightsPanel className="@container w-full">
      <InsightsPanelHeader title="Overview" />
      <InsightsPanelContent className="overflow-hidden">
        <OverviewContent status={status} data={data} />
      </InsightsPanelContent>
    </InsightsPanel>
  )
}

function OverviewContent({ status, data }: { status: Status; data?: OverviewData }) {
  if (status === "loading") return <OverviewSkeleton />
  if (status === "error") return <InsightsPanelErrorDefault />
  if (status === "noData" || !data) {
    data = { activeUserCount: 0, orderCount: 0, averageOrderCost: 0, countriesShippedToCount: 0 }
  }

  const items: StatItem[] = [
    {
      label: "Active users",
      value: Format.number(data.activeUserCount),
      icon: UserCircleIcon,
      badgeClass: "bg-blue-50 text-blue-600",
      hint: "Number of unique users who placed a Storefront order",
    },
    {
      label: "Orders placed",
      value: Format.number(data.orderCount),
      icon: CubeIcon,
      badgeClass: "bg-magenta-100 text-magenta-500",
    },
    {
      label: "Average order cost",
      value: Format.currency(data.averageOrderCost),
      icon: CurrencyDollarIcon,
      badgeClass: "bg-yellow-50 text-yellow-400",
      hint: "Includes product, shipping and taxes.",
    },
    {
      label: "Countries shipped to",
      value: Format.number(data.countriesShippedToCount),
      icon: GlobeIcon,
      badgeClass: "bg-lilac-100 text-lilac-500",
    },
  ]

  return (
    <div className="flex h-full w-full items-center p-6">
      <div className="grid w-full grid-cols-1 gap-4 @md:grid-cols-2 @3xl:grid-cols-4">
        {items.map((it) => (
          <Metric key={it.label} {...it} />
        ))}
      </div>
    </div>
  )
}

type StatItem = {
  label: string
  value: string
  icon: React.ComponentType<{ className?: string }>
  badgeClass: string
  hint?: string
}

function Metric({ label, value, icon: Icon, badgeClass, hint }: StatItem) {
  return (
    <div className="flex items-center gap-3">
      <div className={twMerge("grid size-12 shrink-0 place-items-center rounded-full", badgeClass)}>
        <span className="size-8 [&>svg]:h-full [&>svg]:w-full">
          {Icon && <Icon className="size-8" aria-hidden="true" />}
        </span>
      </div>

      <div className="min-w-0">
        <div className="truncate text-2xl font-medium tracking-tight text-neutral-900">{value}</div>
        <div className="text-neutral-500">
          <span>{label}</span>
          {hint ? (
            <span className="whitespace-nowrap">
              &nbsp;
              <Hint tipText={hint} />
            </span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function Hint({ tipText }: { tipText: string }) {
  return (
    <Bee.Cue
      className="inline-block align-middle"
      tipProps={{ className: "max-w-none" }}
      tip={<div className="font-regular text-gray-900">{tipText}</div>}
      placement="top"
      tipIcon={<QuestionMarkCircleIcon className="h-4 w-4 text-gray-400" />}
    />
  )
}
function OverviewSkeleton() {
  return (
    <div className="flex h-full w-full items-center px-5 py-2">
      <div className="grid w-full grid-cols-4 gap-4 max-lg:grid-cols-2 max-sm:grid-cols-1">
        {[0, 1, 2, 3].map((i) => (
          <SkeletonCell key={i} id={i} />
        ))}
      </div>
    </div>
  )
}

function SkeletonCell({ id }: { id: number }) {
  return (
    <div className="flex items-center justify-start gap-3 py-3" key={id}>
      <div className="size-9 animate-pulse rounded-full border border-neutral-100 bg-neutral-100" />
      <div className="space-y-1">
        <div className="h-3 w-12 animate-pulse rounded bg-neutral-100" />
        <div className="h-4 w-28 animate-pulse rounded bg-neutral-100" />
      </div>
    </div>
  )
}
